import React, { useCallback, useContext } from "react";
import { CloseIcon } from "@icons";
import { Button } from "@design-system/Button/Button";
import { Tag } from "@design-system/Tag/Tag";
import { HStack } from "@design-system/HStack/HStack";
import { AccordionRoot } from "@design-system/Accordion/Accordion";
import { DirectoryContext } from "./Directory.context";
import { CounterpartyDirectoryViewRow } from "./counterpartyDirectoryViewTypes";
import { CounterpartyDetailsCommandBar } from "./CounterpartyDetailsCommandBar";
import { CounterpartyDetailsAccordionItem } from "./CounterpartyDetailsAccordionItem";
import { CounterpartyReviewAccordionItem } from "./CounterpartyReviewAccordionItem";
import styles from "./CounterpartyDetailsPanel.module.scss";

const ACCORDION_DEFAULT_OPEN = ["details", "review"];

interface InnerProps {
    row: CounterpartyDirectoryViewRow;
}

const CounterpartyDetailsPanelInner: React.FC<InnerProps> = ({ row }) => {
    const { setSelectedRow, setEditedItem } = useContext(DirectoryContext);

    const handleEdit = useCallback(() => {
        if (row.counterparty) {
            setEditedItem(row.counterparty);
        }
    }, [row.counterparty, setEditedItem]);

    const cp = row.counterparty;

    return (
        <>
            <div className={styles.panel}>
                <HStack
                    gap={8}
                    align="start"
                    justify="between"
                    paddingInline={24}
                    paddingBlock={16}
                >
                    <HStack gap={8} align="center">
                        <h3 className={styles.name}>{row.name}</h3>
                        {cp?.disabledAt && (
                            <Tag color="neutral" size="sm">
                                Disabled
                            </Tag>
                        )}
                    </HStack>
                    <Button
                        variant="secondary"
                        size="sm"
                        iconOnly
                        onClick={() => setSelectedRow(null)}
                        aria-label="Close panel"
                    >
                        <CloseIcon />
                    </Button>
                </HStack>

                <AccordionRoot
                    multiple
                    defaultValue={ACCORDION_DEFAULT_OPEN}
                    className={styles.detailsAccordion}
                >
                    <CounterpartyDetailsAccordionItem row={row} />
                    {cp && <CounterpartyReviewAccordionItem row={row} />}
                </AccordionRoot>
            </div>
            <CounterpartyDetailsCommandBar
                row={row}
                showEdit={Boolean(cp)}
                onEdit={handleEdit}
            />
        </>
    );
};

export const CounterpartyDetailsPanel: React.FC = () => {
    const { selectedRow } = useContext(DirectoryContext);

    if (!selectedRow || selectedRow.isNoCounterparty) {
        return null;
    }

    return <CounterpartyDetailsPanelInner row={selectedRow} />;
};
